import { ApiClient } from '../utils/api';
import { AuthService } from './AuthService';

/** Options accepted by the TelemetryService constructor */
interface TelemetryOptions {
  apiClient: ApiClient;
  authService: AuthService;
  flushIntervalMs?: number;  // default 10 s
  maxBatchSize?: number;     // default 50 events
}

/** Canonical shape of a telemetry event sent to the backend */
interface TelemetryEvent {
  id: string;               // GUID for idempotency
  name: string;             // e.g. "enemy_killed"
  timestamp: string;        // ISO‑8601 UTC
  sessionId: string;
  userId?: string | null;
  build: string;
  platform: string;
  locale: string;
  payload: Record<string, unknown>;
}

/**
 * TelemetryService
 * ----------------
 *  • Local in‑memory queue with offline persistence (localStorage).
 *  • Batching + optional immediate flush when batchSize reached.
 *  • Exponential back‑off retry (1 s → 60 s).
 *  • Automatic context injection (sessionId, build, platform, locale, userId).
 */
export class TelemetryService {
  private apiClient: ApiClient;
  private authService: AuthService;
  private flushIntervalMs: number;
  private maxBatchSize: number;
  private queue: TelemetryEvent[] = [];
  private timer: ReturnType<typeof setInterval> | null = null;
  private backoff = 1_000;           // exponential back‑off starting at 1 s
  private readonly MAX_BACKOFF = 60_000;

  constructor(opts: TelemetryOptions) {
    this.apiClient       = opts.apiClient;
    this.authService     = opts.authService;
    this.flushIntervalMs = opts.flushIntervalMs ?? 10_000;
    this.maxBatchSize    = opts.maxBatchSize ?? 50;

    // Flush when we regain connectivity
    if (typeof window !== 'undefined') {
      window.addEventListener('online',  () => this.flush().catch(() => void 0));
      window.addEventListener('beforeunload', () => { this.persistQueue(); });
    }

    // Reload any persisted offline events
    this.loadPersistedQueue();
  }

  /** Begins the periodic flush timer */
  async start(): Promise<void> {
    if (this.timer) return;

    // prime once so first batch isn't delayed
    await this.flush().catch(() => void 0);
    this.timer = setInterval(() => this.flush().catch(() => void 0), this.flushIntervalMs);
  }

  /** Track an arbitrary event */
  track<EventPayload extends Record<string, unknown>>(
    name: string,
    payload: EventPayload,
    options?: { flushImmediately?: boolean }
  ): void {
    const now        = new Date();
    const sessionId  = this.authService.getSessionToken() ?? '';
    const userId     = (this.authService as any).getUserId?.() ?? null;   // optional helper
    const event: TelemetryEvent = {
      id        : crypto?.randomUUID?.() ?? `${now.getTime()}-${Math.random()}`,
      name,
      timestamp : now.toISOString(),
      sessionId,
      userId,
      build     : this.detectBuild(),
      platform  : this.detectPlatform(),
      locale    : (typeof navigator !== 'undefined') ? navigator.language : 'en-US',
      payload
    };

    this.queue.push(event);

    if (options?.flushImmediately || this.queue.length >= this.maxBatchSize) {
      // fire and forget; let periodic flush handle retries
      this.flush().catch(() => void 0);
    }
  }

  /** Sends all queued events to the backend with retry/back‑off */
  async flush(): Promise<void> {
    if (typeof navigator !== 'undefined' && !navigator.onLine) return;
    if (this.queue.length === 0) return;

    const batch = this.queue.splice(0, this.maxBatchSize);
    try {
      await this.apiClient.post('/api/telemetry/batch', {
        compressed: false,   // placeholder flag until gzip implemented
        events    : batch
      });

      this.backoff = 1_000;          // reset backoff on success
      if (this.queue.length > 0) {
        // more to send—chain another flush without waiting for interval
        await this.flush();
      }
    } catch (err) {
      // Push events back & schedule retry with exponential back‑off
      this.queue.unshift(...batch);
      this.persistQueue();

      setTimeout(() => this.flush().catch(() => void 0), this.backoff);
      this.backoff = Math.min(this.backoff * 2, this.MAX_BACKOFF);
    }
  }

  /** Persist unsent events to localStorage (basic offline buffer) */
  private persistQueue(): void {
    try {
      const raw = JSON.stringify(this.queue);
      localStorage.setItem('oncade_telemetry_queue', raw);
    } catch { /* ignore quota errors */ }
  }

  /** Load events persisted while offline */
  private loadPersistedQueue(): void {
    try {
      const raw = localStorage.getItem('oncade_telemetry_queue');
      if (raw) {
        this.queue = JSON.parse(raw) as TelemetryEvent[];
        localStorage.removeItem('oncade_telemetry_queue');
      }
    } catch { /* ignore corrupt data */ }
  }

  /** Guess build string from global or fallback */
  private detectBuild(): string {
    // If game embeds its build hash globally, surface it; else "unknown"
    // @ts-ignore
    return (globalThis as any).__GAME_BUILD__ ?? 'unknown';
  }

  /** Basic platform detection (browser / node target) */
  private detectPlatform(): string {
    if (typeof navigator !== 'undefined') {
      return navigator.userAgent;
    }
    return 'node';
  }
}